/**
 * Practitioner Hub → Polls.
 *
 * Written for his example: several practitioners needing to settle a date between themselves.
 * So a question's answers can be dates, and the tally shows who picked which — unless the author
 * chose to keep it anonymous.
 *
 * Nothing reaches anyone until an administrator approves it.
 */
import { useCallback, useEffect, useState } from "react";
import {
  BarChart3, Plus, Send, X, Trash2, Check, Calendar, Lock, Eye, ChevronDown, ChevronUp,
} from "lucide-react";
import { apiFetch } from "../marketplace/api";

const BLANK_Q = { type: "choice", prompt: "", options: ["", ""], required: true };
const BLANK = {
  title: "", intro: "", anonymous: false, results_visibility: "answered",
  closes_at: "", questions: [{ ...BLANK_Q }],
};

const STATUS_TEXT = {
  draft: "Not sent yet",
  pending: "Awaiting review",
  open: "Open for answers",
  closed: "Closed",
  changes_requested: "Sent back to you",
  declined: "Not approved",
};

const styles = `
  .pl-root {
    --sand: #faf7f2; --teal: #1a5a44; --teal-mid: #24755a; --white: #ffffff;
    --border: #e4ddd4; --ink: #1a1a16; --ink-mid: #5a5a54; --ink-light: #8a8a80;
    background: var(--sand); min-height: 100%; font-family: 'Source Sans 3', sans-serif;
  }
  .pl-header {
    background: var(--white); border-bottom: 1px solid var(--border); padding: 20px 28px;
    display: flex; justify-content: space-between; align-items: center; gap: 12px; flex-wrap: wrap;
  }
  .pl-header h1 { font-family: 'Literata', serif; font-size: 26px; color: var(--ink); margin: 0; }
  .pl-header p { font-size: 13px; color: var(--ink-mid); margin: 4px 0 0; }
  .pl-body { padding: 18px 28px 48px; display: flex; flex-direction: column; gap: 14px; }
  .pl-section-title {
    font-family: 'Literata', serif; font-size: 15px; color: var(--ink-mid);
    text-transform: uppercase; letter-spacing: 1px; margin: 10px 0 0;
  }
  .pl-btn {
    font-size: 13px; font-weight: 600; padding: 8px 14px; border-radius: 8px;
    border: 1px solid var(--border); background: var(--white); color: var(--ink-mid);
    cursor: pointer; display: inline-flex; align-items: center; gap: 6px;
    transition: border-color 0.15s, color 0.15s, background 0.15s;
  }
  .pl-btn:hover { border-color: var(--teal); color: var(--ink); }
  .pl-btn:disabled { opacity: 0.5; cursor: not-allowed; }
  .pl-btn-primary { background: var(--teal); border-color: var(--teal); color: #fff; }
  .pl-btn-primary:hover { background: var(--teal-mid); border-color: var(--teal-mid); color: #fff; }
  .pl-btn-danger { border-color: #e8b0b0; color: #a02828; }
  .pl-card {
    background: var(--white); border: 1px solid var(--border); border-radius: 12px; padding: 16px 18px;
  }
  .pl-card h3 { font-family: 'Literata', serif; font-size: 18px; color: var(--ink); margin: 0; }
  .pl-chip {
    font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px;
    padding: 3px 9px; border-radius: 11px; border: 1px solid;
  }
  .pl-field { display: flex; flex-direction: column; gap: 5px; margin-bottom: 10px; }
  .pl-field label { font-size: 12px; font-weight: 600; color: var(--ink-mid); }
  .pl-field input, .pl-field select, .pl-field textarea {
    font-family: inherit; font-size: 14px; color: var(--ink); background: var(--white);
    border: 1px solid var(--border); border-radius: 8px; padding: 8px 10px; width: 100%;
  }
  .pl-field input:focus, .pl-field select:focus, .pl-field textarea:focus {
    outline: none; border-color: var(--teal);
  }
  .pl-q {
    border: 1px solid var(--border); border-radius: 10px; padding: 12px 14px;
    margin-bottom: 10px; background: var(--sand);
  }
  .pl-row { display: flex; gap: 8px; align-items: center; margin-bottom: 6px; }
  .pl-bar-track {
    height: 8px; background: #efeae2; border-radius: 4px; overflow: hidden; flex: 1;
  }
  .pl-bar-fill { height: 100%; background: var(--teal); }
  .pl-answer { display: flex; align-items: center; gap: 8px; font-size: 14px; color: var(--ink); margin-bottom: 7px; }
  .pl-meta { font-size: 12px; color: var(--ink-light); margin-top: 6px; }
  .pl-empty { text-align: center; padding: 30px 16px; color: var(--ink-mid); font-size: 14px; }
`;

const chipStyle = (status) => ({
  open:              { background: "#eaf5f0", color: "#1a5a44", borderColor: "#a0d8c4" },
  pending:           { background: "#fdf6e3", color: "#7a5200", borderColor: "#efdca8" },
  changes_requested: { background: "#eef4fd", color: "#1a50a0", borderColor: "#b0cce8" },
  declined:          { background: "#fdf0f0", color: "#a02828", borderColor: "#e8b0b0" },
}[status] || { background: "#f4f2ee", color: "#5a5a54", borderColor: "#e4ddd4" });

export default function PractitionerPollsPage() {
  const [polls, setPolls] = useState([]);
  const [mine, setMine] = useState([]);
  const [showBuilder, setShowBuilder] = useState(false);
  const [form, setForm] = useState(BLANK);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const [openId, setOpenId] = useState(null);

  const load = useCallback(async () => {
    try {
      const d = await apiFetch("/practitioner/polls");
      setPolls(d?.polls || []);
      setMine(d?.mine || []);
    } catch {
      setPolls([]); setMine([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const setQ = (i, patch) => setForm((f) => ({
    ...f, questions: f.questions.map((q, j) => (j === i ? { ...q, ...patch } : q)),
  }));

  const save = async () => {
    setSaving(true); setError("");
    const payload = {
      ...form,
      closes_at: form.closes_at ? new Date(form.closes_at).toISOString() : null,
      questions: form.questions.map((q) => ({
        ...q,
        options: ["choice", "multi", "date"].includes(q.type)
          ? (q.options || []).filter((o) => o.trim())
          : [],
      })),
    };
    try {
      if (editingId) {
        await apiFetch(`/practitioner/polls/${editingId}`, {
          method: "PUT", body: JSON.stringify(payload),
        });
      } else {
        await apiFetch("/practitioner/polls", {
          method: "POST", body: JSON.stringify(payload),
        });
      }
      setNotice("Sent for review. You will hear back by email.");
      setForm(BLANK); setEditingId(null); setShowBuilder(false);
      await load();
    } catch (e) {
      setError(e?.message || "That did not save.");
    }
    setSaving(false);
  };

  const closeIt = async (id) => {
    await apiFetch(`/practitioner/polls/${id}/close`, { method: "POST" });
    setNotice("Closed."); load();
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this poll and any answers to it?")) return;
    await apiFetch(`/practitioner/polls/${id}`, { method: "DELETE" });
    load();
  };

  return (
    <>
      <style>{styles}</style>
      <div className="pl-root" data-testid="practitioner-polls">
        <div className="pl-header">
          <div>
            <h1>Polls</h1>
            <p>Ask the other practitioners a question — a date, a preference, a short survey</p>
          </div>
          <button className="pl-btn pl-btn-primary" data-testid="poll-new-btn"
            onClick={() => { setForm(BLANK); setEditingId(null); setShowBuilder((s) => !s); }}>
            <Plus size={14} /> {showBuilder && !editingId ? "Close" : "New poll"}
          </button>
        </div>

        <div className="pl-body">
          {notice && (
            <div className="pl-card" style={{ background: "#eaf5f0", borderColor: "#a0d8c4" }}
              data-testid="poll-notice">
              <span style={{ fontSize: 13, color: "#1a5a44" }}>{notice}</span>
            </div>
          )}
          {error && (
            <div className="pl-card" style={{ background: "#fdf0f0", borderColor: "#e8b0b0" }}
              data-testid="poll-error">
              <span style={{ fontSize: 13, color: "#a02828" }}>{error}</span>
            </div>
          )}

          {showBuilder && (
            <div className="pl-card" data-testid="poll-builder">
              <h3>{editingId ? "Edit this poll" : "A new poll"}</h3>
              <p style={{ fontSize: 13, color: "#5a5a54", margin: "6px 0 14px" }}>
                It goes to the team for approval, then every practitioner can answer it.
              </p>
              <div className="pl-field">
                <label htmlFor="pl-title">What are you asking about</label>
                <input id="pl-title" value={form.title} data-testid="poll-title"
                  onChange={(e) => set({ title: e.target.value })}
                  placeholder="Date for the spring group session" />
              </div>
              <div className="pl-field">
                <label htmlFor="pl-intro">Anything they should know first</label>
                <textarea id="pl-intro" rows={2} value={form.intro} data-testid="poll-intro"
                  onChange={(e) => set({ intro: e.target.value })} />
              </div>

              {form.questions.map((q, i) => (
                <div className="pl-q" key={i} data-testid={`poll-question-${i}`}>
                  <div className="pl-field">
                    <label>Question {i + 1}</label>
                    <input value={q.prompt} data-testid={`poll-question-prompt-${i}`}
                      onChange={(e) => setQ(i, { prompt: e.target.value })}
                      placeholder="Which evening suits you?" />
                  </div>
                  <div className="pl-row">
                    <select value={q.type} data-testid={`poll-question-type-${i}`}
                      onChange={(e) => setQ(i, {
                        type: e.target.value,
                        options: e.target.value === "text" ? [] : (q.options?.length ? q.options : ["", ""]),
                      })}
                      style={{
                        fontSize: 13, padding: "6px 8px", borderRadius: 8,
                        border: "1px solid #e4ddd4", background: "#fff", color: "#1a1a16",
                      }}>
                      <option value="choice">Pick one</option>
                      <option value="multi">Pick any that apply</option>
                      <option value="date">Pick a date</option>
                      <option value="text">Write an answer</option>
                    </select>
                    <label className="pl-meta" style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <input type="checkbox" checked={q.required}
                        data-testid={`poll-question-required-${i}`}
                        onChange={(e) => setQ(i, { required: e.target.checked })} />
                      Must be answered
                    </label>
                    {form.questions.length > 1 && (
                      <button className="pl-btn pl-btn-danger" style={{ marginLeft: "auto" }}
                        data-testid={`poll-question-remove-${i}`}
                        onClick={() => set({ questions: form.questions.filter((_, j) => j !== i) })}>
                        <Trash2 size={13} />
                      </button>
                    )}
                  </div>
                  {q.type !== "text" && (
                    <>
                      {(q.options || []).map((opt, oi) => (
                        <div className="pl-row" key={oi}>
                          <input
                            type={q.type === "date" ? "date" : "text"}
                            value={opt}
                            data-testid={`poll-option-${i}-${oi}`}
                            onChange={(e) => setQ(i, {
                              options: q.options.map((o, oj) => (oj === oi ? e.target.value : o)),
                            })}
                            placeholder={q.type === "date" ? "" : `Answer ${oi + 1}`}
                            style={{
                              fontSize: 14, padding: "7px 10px", borderRadius: 8,
                              border: "1px solid #e4ddd4", flex: 1, color: "#1a1a16",
                            }}
                          />
                          {q.options.length > 2 && (
                            <button className="pl-btn" data-testid={`poll-option-remove-${i}-${oi}`}
                              onClick={() => setQ(i, {
                                options: q.options.filter((_, oj) => oj !== oi),
                              })}><X size={12} /></button>
                          )}
                        </div>
                      ))}
                      <button className="pl-btn" data-testid={`poll-option-add-${i}`}
                        onClick={() => setQ(i, { options: [...(q.options || []), ""] })}>
                        <Plus size={12} /> {q.type === "date" ? "Another date" : "Another answer"}
                      </button>
                    </>
                  )}
                </div>
              ))}

              <button className="pl-btn" data-testid="poll-add-question"
                onClick={() => set({ questions: [...form.questions, { ...BLANK_Q }] })}>
                <Plus size={13} /> Another question
              </button>

              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px,1fr))", gap: 12, marginTop: 14 }}>
                <div className="pl-field">
                  <label htmlFor="pl-anon">Answers</label>
                  <select id="pl-anon" value={form.anonymous ? "anon" : "named"}
                    data-testid="poll-anonymous"
                    onChange={(e) => set({ anonymous: e.target.value === "anon" })}>
                    <option value="named">Show who answered what</option>
                    <option value="anon">Keep answers anonymous</option>
                  </select>
                </div>
                <div className="pl-field">
                  <label htmlFor="pl-vis">Who sees the running results</label>
                  <select id="pl-vis" value={form.results_visibility}
                    data-testid="poll-visibility"
                    onChange={(e) => set({ results_visibility: e.target.value })}>
                    <option value="answered">Only those who have answered</option>
                    <option value="everyone">Every practitioner</option>
                    <option value="author">Only me, until it closes</option>
                  </select>
                </div>
                <div className="pl-field">
                  <label htmlFor="pl-closes">Closes (optional)</label>
                  <input id="pl-closes" type="datetime-local" value={form.closes_at}
                    data-testid="poll-closes"
                    onChange={(e) => set({ closes_at: e.target.value })} />
                </div>
              </div>

              <div style={{ display: "flex", gap: 8, marginTop: 6, flexWrap: "wrap" }}>
                <button className="pl-btn pl-btn-primary" onClick={save} disabled={saving}
                  data-testid="poll-save">
                  <Send size={13} /> {saving ? "Saving…" : "Send for approval"}
                </button>
                <button className="pl-btn" data-testid="poll-cancel-builder"
                  onClick={() => { setShowBuilder(false); setEditingId(null); setForm(BLANK); }}>
                  Cancel
                </button>
              </div>
            </div>
          )}

          {polls.length > 0 && <p className="pl-section-title">Open to you</p>}
          {polls.map((p) => (
            <PollCard key={p.id} poll={p} open={openId === p.id}
              onToggle={() => setOpenId(openId === p.id ? null : p.id)} onChanged={load} />
          ))}

          {mine.length > 0 && <p className="pl-section-title">Yours</p>}
          {mine.map((p) => (
            <div className="pl-card" key={`mine-${p.id}`} data-testid={`poll-mine-${p.id}`}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                <div>
                  <h3>{p.title}</h3>
                  <p className="pl-meta">
                    {p.questions?.length} question{p.questions?.length === 1 ? "" : "s"} ·{" "}
                    {p.anonymous ? "anonymous" : "named"} ·{" "}
                    {p.closes_at ? `closes ${new Date(p.closes_at).toLocaleString()}` : "closes when you say"}
                  </p>
                </div>
                <span className="pl-chip" style={chipStyle(p.status)}
                  data-testid={`poll-status-${p.id}`}>
                  {STATUS_TEXT[p.status] || p.status}
                </span>
              </div>
              {p.admin_note && p.status !== "open" && (
                <p className="pl-meta" style={{ color: "#7a5200" }}
                  data-testid={`poll-admin-note-${p.id}`}>
                  From the team: {p.admin_note}
                </p>
              )}
              <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
                {p.status === "open" && (
                  <button className="pl-btn" onClick={() => closeIt(p.id)}
                    data-testid={`poll-close-${p.id}`}>Close it now</button>
                )}
                {["draft", "changes_requested", "declined"].includes(p.status) && (
                  <>
                    <button className="pl-btn" data-testid={`poll-edit-${p.id}`}
                      onClick={() => {
                        setEditingId(p.id);
                        setForm({
                          ...BLANK, ...p,
                          closes_at: p.closes_at
                            ? new Date(p.closes_at).toISOString().slice(0, 16) : "",
                        });
                        setShowBuilder(true);
                      }}>Edit</button>
                    <button className="pl-btn pl-btn-danger" onClick={() => remove(p.id)}
                      data-testid={`poll-delete-${p.id}`}><Trash2 size={13} /> Delete</button>
                  </>
                )}
              </div>
            </div>
          ))}

          {polls.length === 0 && mine.length === 0 && !showBuilder && (
            <div className="pl-card pl-empty" data-testid="polls-empty">
              <BarChart3 size={26} style={{ opacity: 0.35, display: "block", margin: "0 auto 10px" }} />
              No polls yet. Start one and the other practitioners can weigh in.
            </div>
          )}
        </div>
      </div>
    </>
  );
}

function PollCard({ poll, open, onToggle, onChanged }) {
  const [detail, setDetail] = useState(null);
  const [answers, setAnswers] = useState({});
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState("");

  useEffect(() => {
    if (!open) return;
    apiFetch(`/practitioner/polls/${poll.id}`).then((d) => {
      setDetail(d);
      setAnswers(d?.my_answers || {});
    }).catch(() => setDetail({ poll }));
  }, [open, poll]);

  const submit = async () => {
    setSaving(true); setProblem("");
    try {
      const r = await apiFetch(`/practitioner/polls/${poll.id}/respond`, {
        method: "POST", body: JSON.stringify({ answers }),
      });
      setDetail((d) => ({ ...d, results: r.results, my_answers: answers }));
      onChanged();
    } catch (e) {
      setProblem(e?.message || "That did not save.");
    }
    setSaving(false);
  };

  const results = detail?.results;
  const closed = poll.status === "closed";

  return (
    <div className="pl-card" data-testid={`poll-card-${poll.id}`}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
        <div>
          <h3>{poll.title}</h3>
          <p className="pl-meta">
            asked by {poll.author_name}
            {poll.i_answered ? " · you have answered" : ""}
            {closed ? " · closed" : ""}
            {poll.anonymous ? " · anonymous" : ""}
          </p>
        </div>
        <button className="pl-btn" onClick={onToggle} data-testid={`poll-open-${poll.id}`}>
          {open ? <>Hide <ChevronUp size={13} /></> : <>{closed ? "See results" : "Answer it"} <ChevronDown size={13} /></>}
        </button>
      </div>

      {open && detail && (
        <div style={{ marginTop: 12, borderTop: "1px solid #e4ddd4", paddingTop: 12 }}>
          {poll.intro && <p style={{ fontSize: 14, color: "#5a5a54", marginTop: 0 }}>{poll.intro}</p>}

          {!closed && (poll.questions || []).map((q) => (
            <div className="pl-field" key={q.id} data-testid={`poll-answer-${q.id}`}>
              <label>{q.prompt}{q.required ? " *" : ""}</label>
              {q.type === "text" ? (
                <textarea rows={2} value={answers[q.id] || ""}
                  data-testid={`poll-answer-text-${q.id}`}
                  onChange={(e) => setAnswers((a) => ({ ...a, [q.id]: e.target.value }))} />
              ) : q.type === "multi" ? (
                q.options.map((o) => (
                  <label className="pl-answer" key={o}>
                    <input type="checkbox"
                      checked={(answers[q.id] || []).includes(o)}
                      data-testid={`poll-answer-multi-${q.id}-${o}`}
                      onChange={(e) => setAnswers((a) => {
                        const cur = a[q.id] || [];
                        return { ...a, [q.id]: e.target.checked ? [...cur, o] : cur.filter((x) => x !== o) };
                      })} />
                    {o}
                  </label>
                ))
              ) : (
                q.options.map((o) => (
                  <label className="pl-answer" key={o}>
                    <input type="radio" name={`${poll.id}-${q.id}`} checked={answers[q.id] === o}
                      data-testid={`poll-answer-choice-${q.id}-${o}`}
                      onChange={() => setAnswers((a) => ({ ...a, [q.id]: o }))} />
                    {q.type === "date"
                      ? <span><Calendar size={12} style={{ display: "inline", marginRight: 5 }} />
                        {new Date(`${o}T12:00:00`).toLocaleDateString("en-US",
                          { weekday: "short", month: "short", day: "numeric", year: "numeric" })}</span>
                      : o}
                  </label>
                ))
              )}
            </div>
          ))}

          {problem && <p className="pl-meta" style={{ color: "#a02828" }}
            data-testid={`poll-answer-error-${poll.id}`}>{problem}</p>}

          {!closed && (
            <button className="pl-btn pl-btn-primary" onClick={submit} disabled={saving}
              data-testid={`poll-submit-${poll.id}`}>
              <Check size={13} /> {saving ? "Saving…" : poll.i_answered ? "Change my answer" : "Send my answer"}
            </button>
          )}

          {results ? (
            <div style={{ marginTop: 16 }} data-testid={`poll-results-${poll.id}`}>
              <p className="pl-section-title" style={{ marginTop: 0 }}>
                <Eye size={12} style={{ display: "inline", marginRight: 5 }} />
                {results.response_count} answered
              </p>
              {results.questions.map((q) => (
                <div key={q.id} style={{ marginTop: 10 }}>
                  <p style={{ fontSize: 14, fontWeight: 600, color: "#1a1a16", margin: "0 0 6px" }}>
                    {q.prompt}
                  </p>
                  {q.counts ? Object.entries(q.counts).map(([opt, n]) => {
                    const total = Math.max(1, results.response_count);
                    return (
                      <div className="pl-row" key={opt}>
                        <span style={{ fontSize: 13, color: "#5a5a54", minWidth: 150 }}>
                          {q.type === "date"
                            ? new Date(`${opt}T12:00:00`).toLocaleDateString("en-US",
                              { month: "short", day: "numeric" })
                            : opt}
                        </span>
                        <span className="pl-bar-track">
                          <span className="pl-bar-fill" style={{ width: `${(n / total) * 100}%` }} />
                        </span>
                        <span style={{ fontSize: 13, color: "#1a1a16", minWidth: 24 }}>{n}</span>
                        {q.who?.[opt]?.length ? (
                          <span className="pl-meta" style={{ marginTop: 0 }}>
                            {q.who[opt].join(", ")}
                          </span>
                        ) : null}
                      </div>
                    );
                  }) : (q.replies || []).map((r, i) => (
                    <p key={i} style={{ fontSize: 13, color: "#5a5a54", margin: "3px 0" }}>
                      “{r.text}”{r.name ? ` — ${r.name}` : ""}
                    </p>
                  ))}
                </div>
              ))}
            </div>
          ) : (
            <p className="pl-meta" data-testid={`poll-results-hidden-${poll.id}`}>
              <Lock size={12} style={{ display: "inline", marginRight: 5 }} />
              {poll.results_visibility === "author"
                ? "The results go to whoever asked until the poll closes."
                : "Answer it to see how everyone else has."}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
